/**
 * The tracker dog's mesh (ROUND8 §3): boxes rigidly skinned to the dog rig (dogRig.ts), one draw call, like the
 * soldier's. A working shepherd in black and tan: the dark saddle over the back and the top of the head is most
 * of what a camera 45 degrees up sees, so on snow, sand and grass alike the dog is a dark shape with a red collar
 * - the enemy's colour, which everything of theirs carries (05 §4.3).
 *
 * The tan is picked per biome, as the uniforms are, so it never sinks into the ground under it: a warm brown on
 * snow and grass, a darker one on sand, where the lighter brown is within 20 dE of the dunes.
 */

import type { BufferGeometry } from 'three';
import { MeshBuilder, place } from '../kit/MeshBuilder';
import type { BiomeName, ColorRef, Palette } from '../palette';
import { D, DOG_BODY_Y, DOG_NOSE, dogRestWorld, FORE_LOWER, HIND_LOWER, PAW_HEEL, PAW_TOE } from './dogRig';

/** The coat's palette references. `saddle` and `tan` are what the tests hold to the ground's colour. */
export interface DogCoat {
  saddle: ColorRef;
  tan: ColorRef;
  /** The black muzzle. */
  mask: ColorRef;
  nose: ColorRef;
  collar: ColorRef;
  /** Inside the mouth: the jaw's top and the muzzle's underside, seen only when it barks. */
  mouth: ColorRef;
}

const TAN: Readonly<Record<BiomeName, ColorRef>> = { arctic: 'skinB.0', desert: 'skinC.1', temperate: 'skinB.0' };

export function dogCoat(biome: BiomeName): DogCoat {
  return {
    saddle: 'boot.1', tan: TAN[biome], mask: 'boot.0', nose: 'ink.0', collar: 'enemyAccent.0', mouth: 'blood.0',
  };
}

/** How tall the dog is drawn, metres: the ear tips standing, and the withers. */
export const DOG_EAR_TIPS = 0.92;
export const DOG_WITHERS = 0.62;

/** Neck and collar lean forward out of the chest by this much, radians. */
const NECK_LEAN = 0.5;

export function buildDogGeometry(p: Palette): BufferGeometry {
  const mb = new MeshBuilder();
  const coat = dogCoat(p.biome);
  const saddle = p.color(coat.saddle);
  const tan = p.color(coat.tan);
  const mask = p.color(coat.mask);
  const mouth = p.color(coat.mouth);
  const at = (bone: number, fn: () => void): void => {
    const w = dogRestWorld(bone);
    mb.setBone(bone).with(place(w.x, w.y, w.z), fn);
  };

  at(D.body, () => {
    mb.box(0.28, 0.3, 0.4, { all: tan, top: saddle }, place(0, -0.13, 0.14)); // chest, deeper than the hips
    mb.box(0.26, 0.26, 0.36, { all: tan, top: saddle }, place(0, -0.12, -0.21)); // hindquarters
    // The saddle: a little wider than the body, so it runs down both flanks from the withers to the croup.
    // Its top is the withers, just over the chest's.
    mb.box(0.3, 0.13, 0.6, { all: saddle, front: tan }, place(0, DOG_WITHERS - DOG_BODY_Y - 0.13, -0.06));
  });
  at(D.neck, () => {
    mb.box(0.17, 0.28, 0.18, { all: tan, back: saddle, top: saddle }, place(0, -0.04, 0, 0, 1, NECK_LEAN));
    mb.box(0.19, 0.05, 0.2, p.color(coat.collar), place(0, 0.02, 0.01, 0, 1, NECK_LEAN));
  });
  at(D.head, () => {
    mb.box(0.2, 0.16, 0.2, { all: tan, top: saddle, back: saddle }, place(0, -0.07, -0.01)); // skull
    // The muzzle ends in the nose; its underside is the roof of the mouth, seen when the jaw drops.
    const [, noseY, noseZ] = DOG_NOSE;
    const muzzle = { all: mask, front: p.color(coat.nose), bottom: mouth };
    mb.box(0.11, 0.09, 0.17, muzzle, place(0, noseY - 0.045, noseZ - 0.085));
    // Ears pricked: four-sided cones turned square to the face, the tips a little apart.
    for (const side of [1, -1]) {
      mb.cylinder(0.055, 0, 0.14, 4, saddle, place(0.065 * side, 0.08, -0.04, Math.PI / 4, 1, 0, -0.14 * side));
    }
  });
  at(D.jaw, () => {
    mb.box(0.1, 0.04, 0.17, { all: mask, top: mouth }, place(0, -0.035, 0.085));
  });
  at(D.tail, () => {
    mb.box(0.07, 0.07, 0.34, { all: saddle, bottom: tan }, place(0, -0.035, -0.17));
  });
  for (const [upper, lower, thick, length] of [
    [D.lFore, D.lForeLow, 0.1, FORE_LOWER], [D.rFore, D.rForeLow, 0.1, FORE_LOWER],
    [D.lHind, D.lHindLow, 0.16, HIND_LOWER], [D.rHind, D.rHindLow, 0.16, HIND_LOWER],
  ] as const) {
    // Upper leg: a forearm in front, a deep haunch behind. Each overlaps the joint so no gap opens as it bends.
    at(upper, () => mb.box(0.1, 0.24, thick, tan, place(0, -0.22, 0)));
    at(lower, () => {
      mb.box(0.08, length - 0.02, 0.09, tan, place(0, -length + 0.02, 0));
      // The paw, flat on the ground at rest, from heel to toe.
      mb.box(0.1, 0.05, PAW_TOE - PAW_HEEL, tan, place(0, -length, (PAW_TOE + PAW_HEEL) / 2));
    });
  }
  return mb.build();
}
